/** @jsxRuntime classic */
/** @jsx jsx */
/* eslint-disable react/no-array-index-key */
import { connect } from 'react-redux';
import { css, jsx } from '@emotion/react';

import { RootState } from 'reducers/rootReducer';
import { PrintedTable, RowTable, PrintedRow } from 'types/printTable';
import { Dimensions } from 'types/dimensions';
import { selectors as printPreviewModalSelectors } from 'ducks/modal/printPreviewModal';
import {
  getAreaTableDefinition, getPrintTable, getTableSize,
} from 'helpers/print/printTable';

export const PRINT_TABLE_CONTAINER = 'printTableContainer';

const tableStyles = css`
  display: none;
  position: absolute;
  width: 100%;
  height: 100%;
`;

const headTextStyles = css`
  text-transform: uppercase;
  font-weight: 600;
  font-size: 12px;
`;

const textStyles = css`
  font-size: 14px;
  font-family: 'Montserrat';
  font-weight: 300;
  padding: 0.5em;
`;

interface StateProps {
  readonly stringTable: string[][] | undefined;
}

type Props = StateProps;

const PrintTableView = ({ stringTable }: Props): (JSX.Element | null) => {
  if (!stringTable) {
    return null;
  }

  const printedTable: PrintedTable = getPrintTable(
    getAreaTableDefinition(),
    stringTable,
  );

  const tableSize: Dimensions = getTableSize();

  const xOffset = 45;

  const PrintRows = PrintTableToColumns(printedTable);
  const TableColumnPositions = [0, 210, 490];

  // Draw Table as Rows
  return (
    <div css={tableStyles}>
      <svg
        id={PRINT_TABLE_CONTAINER}
        width={tableSize.width}
        height={tableSize.height}
      >
        {PrintRows.rows.map((row, rowIndex) => (
          <text
            key={rowIndex}
            x={0}
            y={row.dyPosition}
            css={textStyles}
          >
            {row.cells.map((cell, cellIndex) => (
              <tspan
                key={cellIndex}
                x={TableColumnPositions[cellIndex] + xOffset}
                dy={0}
                css={cell.isBold ? headTextStyles : null}
              >
                {cell.text}
              </tspan>
            ))}
          </text>
        ))}
      </svg>
    </div>
  );
};


const PrintTableToColumns = (printTable: PrintedTable): RowTable => {
  const outTable: RowTable = { rows: [] };
  const numberOfColumnRows = printTable.columns[0].cells.length;

  // the maximum length of text we'll allow in the calculation cell before it overlaps/overflows.
  const maxTextLength = 45;

  // dy in the cell is inherited from the table format. It is not used by the rows.
  const emptyCell = { dy: 0, text: '', isBold: false };

  // because we are adding additional rows as we manually wrap, we need a second counter to keep track
  // of the incrementing dy.
  let numberOfResultingRows = 0;

  for (let i = 0; i < numberOfColumnRows; i++) {
    // First, figure out the original Contents of the Row
    // since it was originally in columns, we select the matching cell from each column.
    const rowCells = [printTable.columns[0].cells[i], printTable.columns[1].cells[i], printTable.columns[2].cells[i]];
    let calculationCellText = rowCells[1].text;

    // Then, determine and handle the initial need of wrapping.
    if (calculationCellText.length > maxTextLength) {
      rowCells[1].text = calculationCellText.substring(0, maxTextLength);
      calculationCellText = calculationCellText.substring(maxTextLength);
    } else {
      calculationCellText = '';
    }

    // Finish adding the original row to the row Table
    const newDy = 18 * (numberOfResultingRows + 1);
    const newRow: PrintedRow = { dyPosition: newDy, cells: rowCells };
    outTable.rows.push(newRow);
    numberOfResultingRows += 1;

    // Manually Handling the Wrapping
    while (calculationCellText.length > 0) {
      // Determine the text for the wrapped rows.
      const wrappedCell = { ...printTable.columns[1].cells[i] };
      if (calculationCellText.length > maxTextLength) {
        wrappedCell.text = calculationCellText.substring(0, maxTextLength);
        calculationCellText = calculationCellText.substring(maxTextLength);
      } else {
        wrappedCell.text = calculationCellText;
        calculationCellText = '';
      }

      // Create and add the next wrapped rows.
      const wrappedCells = [emptyCell, wrappedCell, emptyCell];
      const wrappedDy = 18 * (numberOfResultingRows + 1);
      const wrappedRow: PrintedRow = { dyPosition: wrappedDy, cells: wrappedCells };
      outTable.rows.push(wrappedRow);
      numberOfResultingRows += 1;
    }
  }
  return outTable;
};

export default connect((state: RootState): StateProps => ({
  stringTable: printPreviewModalSelectors.getStringTable(state),
}))(PrintTableView);
